import { formatDate, formatDateTime } from "../../utils/datetime";
import PdfPreview from "./components/PdfPreview";
import PdfPage from "./components/PdfPage";
import styled from "styled-components";
import { Table } from "react-bootstrap";
import AbstractReportHeading from "./components/AbstractReportHeading";
import { InfoHeader, InfoSection } from "../vehicles/components/VehicleInfo";

const S = {
	Note: styled.p`
    font-size: 14px;
    margin-bottom: 12px;
  `,
	Section: styled.div`
    margin-bottom: 20px;
  `,
	Td: styled.td`
    width: 50%;
    padding: 12px 16px;
    vertical-align: top;
    border-bottom: 1px solid #e9ecef;
    color: #495057;
  `,
	Label: styled.td`
    font-weight: 600;
    width: 30%;
    padding: 12px 16px;
    vertical-align: top;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50;
    background-color: #f8f9fa;
  `,
	PhotoContainer: styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 20px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  `,
	PhotoWrapper: styled.div`
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: #f8f9fa;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: translateY(-2px);
    }
  `,
	Photo: styled.img`
    width: 100%;
    height: 260px;
    object-fit: cover;
    display: block;
    object-fit: contain;
    background:rgb(25, 25, 25);
  `,
	PhotoPlaceholder: styled.div`
    width: 100%;
    height: 200px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 14px;
    font-weight: 500;
  `,
	PhotoLabel: styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
  `,
	RequestSection: styled.div`
    padding: 32px;
    border-left: 6px solid #007bff;
    border-radius: 8px;
    margin: 32px 0;
    position: relative;
    
    &::before {
      display: none;
    }
  `,
	RequestContent: styled.div`
    position: relative;
    z-index: 1;
  `,
	TableContainer: styled.div`
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `,
	StyledTable: styled(Table)`
    margin-bottom: 0;
    
    tbody tr:last-child td {
      border-bottom: none;
    }
    
    tbody tr:hover {
      background-color: #f8f9fa;
    }
  `,
	SectionHeader: styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e9ecef;
    
    h5 {
      margin: 0;
      color: #2c3e50;
      font-weight: 600;
      font-size: 20px;
    }
  `,
	SectionIcon: styled.div`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-size: 16px;
    font-weight: bold;
  `,
	AlertBadge: styled.span`
    background: #dc3545;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
  `,
	InfoCard: styled.div`
    padding: 16px;
    margin: 16px 0;
    
    p {
      margin: 0;
      color: #1565c0;
      font-weight: 500;
    }
  `,
};

interface ContainerType {
	id: number;
	containerNumber: string;
	seenAt: string;
	location: string;
	trailerPlate: string;
	truckPlate: string;
	photos: Array<{ id: number; file: string }>;
	intelligence: string;
	vehiclePlates: string[];
	plateLookupResults?: Array<{
		plate: string;
		smviu: { vehicleId: number; stolenStatus: string } | null;
		rdw: { status: "found" | "not-found" };
	}>;
}

interface ReportProps {
	container: ContainerType;
	language?: "en" | "nl" | "fr";
}

const getContent = (language: string = "en") => {
	const content = {
		en: {
			title: "Container Alert Report",
			investigationText: `An ongoing investigation into organized vehicle theft has uncovered a trafficking operation in which stolen Dutch vehicles are being concealed in shipping containers and shipped to West Africa. Digitpol, in cooperation with the Stichting Verzekeringsbureau Voertuigcriminaliteit (VbV), is actively conducting surveillance at locations where such vehicles are believed to be loaded into containers. While the primary objective is to intercept and seize the vehicles at these locations, the modus operandi and timing deployed by the suspects often make on-site intervention unfeasible.`,
			containerDetails: "Container Details",
			observationDate: "Observation Date",
			containerNumber: "Container Number",
			location: "Location",
			trailerPlate: "Trailer Plate",
			truckPlate: "Truck Plate",
			intelligence: "Intelligence Notes",
			vehiclePlates: "Vehicle Plates Found",
			photos: "Evidence Photos",
			requestTitle: "Port Authority Request",
			requestText: `Based on the intelligence gathered, we have substantial grounds to believe that the containers may contain stolen vehicles. We respectfully request that the listed containers be identified, secured, and subjected to a thorough inspection upon arrival at your port. We further request that the outcome of the inspection be communicated to the relevant Dutch authorities, enabling us to conclude this investigative request.`,
			keywords: "Keywords",
			publicationDate: "Publication Date",
			alertLevel: "HIGH PRIORITY",
		},
		nl: {
			title: "Container Waarschuwingsrapport",
			investigationText: `Een lopend onderzoek naar georganiseerde voertuigdiefstal heeft een smokkeloperatie aan het licht gebracht waarbij gestolen Nederlandse voertuigen worden verborgen in zeecontainers en naar West-Afrika worden verscheept. Digitpol voert, in samenwerking met de Stichting Verzekeringsbureau Voertuigcriminaliteit (VbV), actief surveillance uit op locaties waar dergelijke voertuigen naar verwachting in containers worden geladen. Hoewel het primaire doel is om de voertuigen op deze locaties te onderscheppen en in beslag te nemen, maken de werkwijze en timing van de verdachten interventie ter plaatse vaak onhaalbaar.`,
			containerDetails: "Container Details",
			observationDate: "Observatiedatum",
			containerNumber: "Containernummer",
			location: "Locatie",
			trailerPlate: "Trailernummer",
			truckPlate: "Vrachtwagennummer",
			intelligence: "Intelligence Notities",
			vehiclePlates: "Gevonden Kentekens",
			photos: "Bewijs Foto's",
			requestTitle: "Verzoek aan Havenautorit",
			requestText: `Op basis van de verzamelde inlichtingen hebben wij substantiële gronden om te geloven dat de containers gestolen voertuigen kunnen bevatten. Wij verzoeken u respectvol om de genoemde containers te identificeren, veilig te stellen en grondig te inspecteren bij aankomst in uw haven. Wij verzoeken u verder om de uitkomst van de inspectie te communiceren aan de relevante Nederlandse autoriteiten, zodat wij dit onderzoeksverzoek kunnen afronden.`,
			keywords: "Trefwoorden",
			publicationDate: "Publicatiedatum",
			alertLevel: "HOGE PRIORITEIT",
		},
		fr: {
			title: "Rapport d'Alerte de Conteneur",
			investigationText: `Une enquête en cours sur le vol organisé de véhicules a révélé une opération de trafic dans laquelle des véhicules néerlandais volés sont dissimulés dans des conteneurs d'expédition et expédiés vers l'Afrique de l'Ouest. Digitpol, en coopération avec la Stichting Verzekeringsbureau Voertuigcriminaliteit (VbV), mène activement une surveillance dans les lieux où de tels véhicules sont supposés être chargés dans des conteneurs. Bien que l'objectif principal soit d'intercepter et de saisir les véhicules sur ces sites, le modus operandi et le timing déployés par les suspects rendent souvent l'intervention sur site irréalisable.`,
			containerDetails: "Détails du Conteneur",
			observationDate: "Date d'Observation",
			containerNumber: "Numéro de Conteneur",
			location: "Emplacement",
			trailerPlate: "Plaque de Remorque",
			truckPlate: "Plaque de Camion",
			intelligence: "Notes de Renseignement",
			vehiclePlates: "Plaques de Véhicules Trouvées",
			photos: "Photos de Preuve",
			requestTitle: "Demande à l'Autorité Portuaire",
			requestText: `Sur la base des renseignements recueillis, nous avons des motifs substantiels de croire que les conteneurs peuvent contenir des véhicules volés. Nous demandons respectueusement que les conteneurs listés soient identifiés, sécurisés et soumis à une inspection approfondie à leur arrivée dans votre port. Nous demandons en outre que le résultat de l'inspection soit communiqué aux autorités néerlandaises compétentes, nous permettant de conclure cette demande d'enquête.`,
			keywords: "Mots-clés",
			publicationDate: "Date de Publication",
			alertLevel: "PRIORITÉ ÉLEVÉE",
		},
	};

	return content[language as keyof typeof content] || content.en;
};

const ContainerAlertReport = ({ container, language = "en" }: ReportProps) => {
	const content = getContent(language);
	const today = formatDate(new Date());

	const handleImageError = (
		event: React.SyntheticEvent<HTMLImageElement, Event>,
	) => {
		const img = event.target as HTMLImageElement;
		img.style.display = "none";
		const placeholder = img.nextElementSibling as HTMLElement;
		if (placeholder) {
			placeholder.style.display = "flex";
		}
	};

	const containerDetailsFields = [
		{ key: "containerNumber", label: content.containerNumber },
		{
			key: "seenAt",
			label: content.observationDate,
			formatter: formatDateTime,
		},
		{ key: "location", label: content.location },
		{ key: "trailerPlate", label: content.trailerPlate },
		{ key: "truckPlate", label: content.truckPlate },
		{
			key: "vehiclePlates",
			label: content.vehiclePlates,
			formatter: (v) => (v && v.length > 0 ? v.join(", ") : undefined),
		},
		{ key: "intelligence", label: content.intelligence },
	];

	const ContainerInfo = ({ container }) => {
		const fields = containerDetailsFields
			.map(({ key, label, formatter }) => ({
				key,
				value: container[key],
				label,
				formatter,
			}))
			.filter(
				({ value }) => value !== undefined && value !== null && value !== "",
			);
		return (
			<InfoSection
				header={content.containerDetails}
				fields={fields}
				labelFn={(key) => fields.find((f) => f.key === key)?.label || key}
				valueFn={(key, value) => {
					const field = fields.find((f) => f.key === key);
					return field && field.formatter ? field.formatter(value) : value;
				}}
			/>
		);
	};

	return (
		<PdfPreview>
			<PdfPage>
				<AbstractReportHeading
					fileName={content.title}
					leftTitle={container.containerNumber}
					date={today}
					refValue={`#CONT-${container.id}`}
					subject={`Container Alert - ${container.containerNumber}`}
					extraRows={[
						{
							label: "Container",
							value: `${container.containerNumber} – ${container.location}`,
						},
					]}
				/>

				<S.Section>
					<S.Note>
						{content.alertLevel} - This container requires immediate attention
					</S.Note>

					<S.Note>{content.investigationText}</S.Note>

					<S.Note>
						On{" "}
						<strong style={{ color: "#2c3e50" }}>
							{formatDate(container.seenAt)}
						</strong>
						, the following container was observed with international serial
						number{" "}
						<strong style={{ color: "#2c3e50" }}>
							{container.containerNumber}
						</strong>
						. This container could be associated with the same organized crime
						group previously linked to the illicit export of stolen vehicles.
					</S.Note>
				</S.Section>

				<S.Section>
					<ContainerInfo container={container} />
				</S.Section>

				<S.Section>
					<InfoHeader header={content.requestTitle} />
					<S.Note>{content.requestText}</S.Note>
				</S.Section>
			</PdfPage>

			{/* Dynamically render evidence photos, 6 per page */}
			{container.photos &&
				container.photos.length > 0 &&
				container.photos
					.reduce((pages, photo, idx) => {
						const pageIdx = Math.floor(idx / 6);
						if (!pages[pageIdx]) pages[pageIdx] = [];
						pages[pageIdx].push(photo);
						return pages;
					}, [])
					.map((photosOnPage, pageIdx) => (
						<PdfPage
							key={
								photosOnPage[0]?.id
									? `evidence-${photosOnPage[0].id}`
									: `evidence-page-${pageIdx}`
							}
						>
							<S.Section>
								<InfoHeader header={content.photos} />
								<S.PhotoContainer>
									{photosOnPage.map((photo) => (
										<S.PhotoWrapper key={photo.id}>
											<S.Photo
												src={photo.file}
												alt={`Container evidence ${photo.id}`}
												onError={handleImageError}
											/>
											<S.PhotoPlaceholder style={{ display: "none" }}>
												Photo {photo.id} - Image unavailable
											</S.PhotoPlaceholder>
											<S.PhotoLabel>Evidence #{photo.id}</S.PhotoLabel>
										</S.PhotoWrapper>
									))}
								</S.PhotoContainer>
							</S.Section>
						</PdfPage>
					))}
		</PdfPreview>
	);
};

export default ContainerAlertReport;
